import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';

import { ErrorToast } from 'components';
import PemeStatusService from 'api/peme-status';
import EmployeesApi from 'api/employees';
import DepartmentService from 'api/department';

import PipelineOverviewCards from './components/pipeline-overview-cards/PipelineOverviewCards';
import PemeKpiStats from './components/peme-kpi-stats/PemeKpiStats';
import PemeStatusTabs from './components/peme-status-tabs/PemeStatusTabs';
import PemeStatusTable from './components/peme-status-table/PemeStatusTable';
import MemberSearchBar from '../employees/employeesOverview/components/member-search-bar/MemberSearchBar';
import TablePagination from '../employees/employeesOverview/components/table-pagination/TablePagination';
import PemeProfileDrawer from './components/peme-profile-drawer';
import PemeCrewStatusModal from './components/peme-crew-status-modal';
import CrewInviteModal from '../employees/employeesOverview/components/crew-invite-modal/CrewInviteModal';

import './peme-status-page.scss';

const ITEMS_PER_PAGE = 20;

// Static map — moved outside component to avoid stale closure in useCallback
const TAB_TO_STATUS_MAP = {
  all: undefined,
  scheduled: 'Scheduled',
  inClinic: 'In Clinic',
  pending: 'Pending',
};

const PemeStatusPage = () => {
  const userInfo = useSelector((state) => state.general.user);
  const isAdmin = userInfo?.metadata?.role === 'admin' || userInfo?.email?.endsWith('@riverr.ai');
  const isRiverrUser = userInfo?.email?.endsWith('@riverr.ai');

  // Data state
  const [pipelineStages, setPipelineStages] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter state
  const [activeTab, setActiveTab] = useState('all');
  const [stageFilter, setStageFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activePage, setActivePage] = useState(1);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [profileDrawerMember, setProfileDrawerMember] = useState(null);
  const [profileDrawerTab, setProfileDrawerTab] = useState('Overview');
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCrew, setEditingCrew] = useState(null);
  const [editMemberModalOpen, setEditMemberModalOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [employeesMap, setEmployeesMap] = useState({});

  const isFirstRender = useRef(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page: activePage,
        limit: ITEMS_PER_PAGE,
        status: TAB_TO_STATUS_MAP[activeTab],
        stage: stageFilter,
        search: searchQuery || undefined,
      };

      // Fetch all PEME employee IDs (unpaginated) to know who's tracked
      const allPemeParams = { page: 1, limit: 1000 };

      const [pipelineRes, crewRes, statsRes, employeesRes, allPemeRes] = await Promise.all([
        PemeStatusService.getPipelineOverview(),
        PemeStatusService.getCrewStatuses(params),
        PemeStatusService.getStats(),
        EmployeesApi.getAll({ filters: { isDeleted: false }, limit: 1000, offset: 0 }),
        PemeStatusService.getCrewStatuses(allPemeParams),
      ]);

      // Build employee lookup map by _id
      const employees = employeesRes?.data?.data || [];
      const empMap = {};
      employees.forEach((emp) => {
        empMap[emp._id] = emp;
      });
      setEmployeesMap(empMap);

      // Build tracking set from ALL PEME records (not just current page)
      const allPemeRecords = allPemeRes?.data || [];
      const trackedEmpIds = new Set(allPemeRecords.map((item) => item.employeeId));

      // Current page records for display
      const pemeRecords = crewRes?.data || [];

      // Enrich PEME records with employee data
      const enrichedPeme = pemeRecords.map((item) => {
        const emp = empMap[item.employeeId];
        if (emp) {
          return {
            ...item,
            name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || item.name,
            email: emp.email || item.email,
            rank: emp.designation || item.rank,
            embarkationDate: emp.signOnDate || item.embarkationDate,
            vessel: emp.metadata?.vessel || item.vessel || '',
          };
        }
        return item;
      });

      // Add employees WITHOUT PEME records as "Pending Scheduling"
      const untrackedEmployees = employees
        .filter((emp) => !trackedEmpIds.has(emp._id))
        .map((emp) => ({
          id: `untracked-${emp._id}`,
          employeeId: emp._id,
          name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
          email: emp.email || '',
          rank: emp.designation || '',
          embarkationDate: emp.signOnDate || '',
          vessel: emp.metadata?.vessel || '',
          status: 'Pending Scheduling',
          currentStage: '',
          daysInStage: 0,
          isOverdue: false,
          stageHistory: [],
          notes: '',
        }));

      const allCrew = [...enrichedPeme, ...untrackedEmployees];

      // Recompute status counts to include untracked employees
      const counts = crewRes?.statusCounts || {};
      counts.scheduled = (counts.scheduled || 0) + untrackedEmployees.length;
      counts.all = (counts.all || 0) + untrackedEmployees.length;

      setPipelineStages(pipelineRes?.stages || []);
      setCrewList(allCrew);
      setTotalRecords((crewRes?.total || 0) + untrackedEmployees.length);
      setStatusCounts(counts);
      setStats(statsRes || null);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    } finally {
      setIsLoading(false);
    }
  }, [activePage, activeTab, stageFilter, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch departments once for Edit Member modal
  useEffect(() => {
    DepartmentService.getAll()
      .then((res) => setDepartments(res?.data?.data || []))
      .catch(() => {});
  }, []);

  // Reset page to 1 on filter changes (skip first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setActivePage(1);
  }, [activeTab, stageFilter, searchQuery]);

  const debouncedSetSearch = useCallback(
    debounce((value) => setSearchQuery(value), 300),
    []
  );

  // Cancel debounce on unmount to prevent state update on unmounted component
  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  const handleSearchChange = (value) => {
    setSearchInputValue(value);
    debouncedSetSearch(value);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setStageFilter(null);
    setExpandedRowId(null);
  };

  const handleStageFilter = (stage) => {
    setStageFilter(stage === stageFilter ? null : stage);
  };

  const openProfileDrawer = (member, tab = 'Overview') => {
    setProfileDrawerMember(member);
    setProfileDrawerTab(tab);
    setProfileDrawerOpen(true);
  };

  const closeProfileDrawer = () => {
    setProfileDrawerOpen(false);
    setProfileDrawerMember(null);
  };

  const handleEditProfile = (crew) => {
    const emp = employeesMap[crew.employeeId];
    if (emp) {
      setEditMember(emp);
      setProfileDrawerOpen(false);
      setEditMemberModalOpen(true);
    }
  };

  const handleEdit = (crew) => {
    setEditingCrew(crew);
    setIsModalOpen(true);
  };

  const handleModalSave = async (payload) => {
    try {
      if (editingCrew) {
        await PemeStatusService.updateCrewStatus(editingCrew.id, payload);
        toast.success('PEME record updated');
      } else {
        await PemeStatusService.createCrewStatus(payload);
        toast.success('PEME record created');
      }
      setIsModalOpen(false);
      setEditingCrew(null);
      fetchData();
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  const handleStatusUpdate = async (id, updateData) => {
    try {
      await PemeStatusService.updateCrewStatus(id, updateData);
      toast.success('Status updated successfully');
      fetchData();
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE);

  return (
    <div className="peme-status-page">
      {/* Header */}
      <div className="peme-status-page__header tw-flex tw-items-start tw-justify-between">
        <div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-m-0">PEME Status</h1>
          <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-0">
            Current PEME status for crew candidates.
          </p>
        </div>
        {isRiverrUser && (
          <button
            onClick={() => { setEditingCrew(null); setIsModalOpen(true); }}
            className="tw-px-4 tw-py-2 tw-bg-indigo-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-indigo-700 tw-border-0 tw-cursor-pointer tw-transition-colors"
          >
            + Create PEME
          </button>
        )}
      </div>

      {/* Pipeline Overview Cards */}
      <PipelineOverviewCards stages={pipelineStages} isLoading={isLoading} />

      {/* KPI Stats */}
      <PemeKpiStats stats={stats} isLoading={isLoading} />

      {/* Tabs + Search */}
      <div className="tw-mt-6">
        <p className="tw-text-xs tw-uppercase tw-tracking-widest tw-font-semibold tw-text-gray-400 tw-mb-3">
          Recent Crew Status Updates
        </p>
        <PemeStatusTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          counts={statusCounts}
          stageFilter={stageFilter}
          onStageFilter={handleStageFilter}
          pipelineStages={pipelineStages}
        />
      </div>

      <div className="tw-mt-4 tw-mb-4">
        <MemberSearchBar
          searchValue={searchInputValue}
          onSearchChange={handleSearchChange}
          hideFilters
        />
      </div>

      {/* Table */}
      <PemeStatusTable
        crewList={crewList}
        isLoading={isLoading}
        isAdmin={isRiverrUser}
        expandedRowId={expandedRowId}
        onExpandRow={(id) => setExpandedRowId(id === expandedRowId ? null : id)}
        onEdit={handleEdit}
        onViewProfile={(member) => openProfileDrawer(member, 'Overview')}
        onViewDocuments={(member) => openProfileDrawer(member, 'Documents')}
        onStatusUpdate={handleStatusUpdate}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <TablePagination
          activePage={activePage}
          totalPages={totalPages}
          totalRecords={totalRecords}
          pageSize={ITEMS_PER_PAGE}
          onPageClick={setActivePage}
        />
      )}

      {/* Profile Drawer */}
      {profileDrawerOpen && profileDrawerMember && (
        <PemeProfileDrawer
          open={profileDrawerOpen}
          onClose={closeProfileDrawer}
          crew={profileDrawerMember}
          onEditProfile={handleEditProfile}
        />
      )}

      {/* Edit Member Modal (same as Members page) */}
      {editMemberModalOpen && (
        <CrewInviteModal
          open={editMemberModalOpen}
          onClose={() => { setEditMemberModalOpen(false); setEditMember(null); }}
          member={editMember}
          fetchEmployees={fetchData}
          departments={departments}
        />
      )}

      {/* Create/Edit PEME Modal */}
      <PemeCrewStatusModal
        open={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingCrew(null); }}
        editingCrew={editingCrew}
        onSave={handleModalSave}
      />
    </div>
  );
};

export default PemeStatusPage;
